import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import MobileMenu from '../components/MobileMenu';
import Icon from '../components/Icon';
import Logo from '../components/Logo';
import useSEO from '../hooks/useSEO';

const TermsPage = () => {
  const navigate = useNavigate();

  // SEO optimization
  useSEO({
    title: 'Användarvillkor - Your Therapist | AI-terapi tjänstevillkor',
    description: 'Läs våra användarvillkor för AI-driven terapi. Villkor för användning av vår plattform för mental hälsovård och terapitjänster.',
    keywords: 'användarvillkor, tjänstevillkor, AI-terapi villkor, användaravtal, terapi avtal, mental hälsa villkor',
    image: 'https://www.yourtherapist.se/logo512.png',
    url: 'https://www.yourtherapist.se/terms',
    type: 'website'
  });

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <Logo 
              size="large"
              onClick={handleBackToHome}
              textColor="text-zink-700"
              hoverTextColor="hover:text-pink-600"
            />
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <button
                onClick={handleBackToHome}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-pink-600 transition-colors font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Tillbaka hem
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <MobileMenu />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-24 pb-16 max-w-4xl mx-auto px-6">
        <div className="prose prose-lg max-w-none text-black">
          <h1 className="text-4xl font-bold mb-8 text-center text-black">Användarvillkor för klienter</h1>
          
          <p className="text-center text-gray-600 mb-12">
            Senast uppdaterad: 13 augusti 2025
          </p>

          <div className="space-y-8 text-black leading-relaxed">
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-black">1. Allmänna villkor</h2>
              <p>
                Dessa användarvillkor ("Villkoren") reglerar din användning av Your Therapist tjänster ("Tjänsten"), 
                som tillhandahålls av Your Therapist AB ("vi", "oss", "vår", "Företaget"). Genom att använda 
                Tjänsten accepterar du dessa Villkor i sin helhet.
              </p>
              <p>
                Tjänsten erbjuder AI-driven terapeutisk support i kombination med legitimerade terapeuters 
                professionella vägledning. Du måste vara minst 18 år gammal för att använda Tjänsten.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-black">2. Kontoskapande och användaransvar</h2>
              <p>
                För att använda Tjänsten måste du skapa ett konto med korrekt och aktuell information. 
                Du är ansvarig för att hålla dina inloggningsuppgifter säkra och för all aktivitet som 
                sker under ditt konto.
              </p>
              <p>
                Du förbinder dig att använda Tjänsten endast för dess avsedda ändamål - det vill säga 
                för terapeutisk support och mental hälsovård. Du får inte använda Tjänsten för illegala 
                aktiviteter, spam, eller på något sätt som kan skada Tjänsten eller andra användare.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-black">3. Tjänstens omfattning och begränsningar</h2>
              <p>
                Your Therapist tillhandahåller AI-baserad terapeutisk support som komplement till, 
                men inte ersättning för, professionell terapi. Tjänsten inkluderar:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>AI-driven chattfunktion för terapeutisk support</li>
                <li>Möjlighet att kommunicera med legitimerade terapeuter</li>
                <li>Framstegsspårning och personaliserad vägledning</li>
                <li>Säker och krypterad kommunikation</li>
              </ul>
              <p>
                Vi reserverar oss rätten att när som helst modifiera, pausa eller avbryta Tjänsten 
                med eller utan förvarning, även om vi strävar efter att ge rimlig förvarning när möjligt.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-black">4. AI-teknologi och ansvarsbegränsning</h2>
              <p className="font-semibold text-red-700">
                VIKTIGT: AI-chatten är en automatiserad tjänst som kan ge felaktig, ofullständig 
                eller olämplig information.
              </p>
              <p>
                Vår AI-teknologi är utformad för att ge stödjande och terapeutisk vägledning, men den 
                är inte felfri. AI:n kan:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Ge felaktiga eller vilseledande råd</li>
                <li>Missförstå eller misstolka dina meddelanden</li>
                <li>Ge svar som inte är lämpliga för din specifika situation</li>
                <li>Ha tekniska problem eller vara otillgänglig</li>
              </ul>
              <p className="font-semibold">
                Du använder AI-chatten på egen risk. Vi ansvarar inte för eventuella skador, 
                fysiska eller psykiska, som kan uppstå till följd av AI:ns råd eller brist på råd.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-black">5. Medicinsk friskrivning</h2>
              <p className="font-semibold text-red-700">
                Tjänsten utgör inte medicinsk rådgivning och ska inte användas som ersättning för 
                professionell medicinsk eller psykiatrisk vård.
              </p>
              <p>
                Om du upplever en medicinsk eller psykiatrisk nödsituation, kontakta omedelbart:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>112 för akuta nödsituationer</li>
                <li>1177 för sjukvårdsrådgivning</li>
                <li>90 101 för självmordspreventionslinjen</li>
                <li>Din lokala vårdcentral eller psykiatriska akutmottagning</li>
              </ul>
              <p>
                Vi rekommenderar starkt att du kombinerar användningen av Tjänsten med reguljär 
                kontakt med legitimerad vårdpersonal för din mentala hälsa.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-black">6. Förbjuden användning</h2>
              <p>Du får INTE använda Tjänsten för att:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Planera eller genomföra självskada eller skada mot andra</li>
                <li>Dela eller sprida AI-genererat innehåll som ditt eget</li>
                <li>Försöka manipulera eller "hacka" AI-systemet</li>
                <li>Dela känslig information om tredje parter utan deras samtycke</li>
                <li>Använda Tjänsten för kommersiella ändamål utan vårt skriftliga tillstånd</li>
                <li>Kränka andras rättigheter eller bryta mot tillämplig lag</li>
                <li>Skapa multipla konton eller dela ditt konto med andra</li>
              </ul>
              <p className="font-semibold">
                Vi förbehåller oss rätten att omedelbart avsluta ditt konto om du bryter mot 
                dessa regler.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-black">7. Betalning och återbetalning</h2>
              <p>
                Betalning för Tjänsten sker enligt de priser och villkor som anges på vår webbplats. 
                Alla avgifter är icke-återbetalningsbara såvida inte annat uttryckligen anges eller 
                krävs enligt lag.
              </p>
              <p>
                Vi kan ändra våra priser med 30 dagars skriftlig förvarning. Prisändringar gäller 
                endast för nya faktureringsperioder.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-black">8. Personuppgifter och integritet</h2>
              <p>
                Din integritet är viktig för oss. Behandlingen av dina personuppgifter regleras av 
                vår Integritetspolicy, som utgör en integrerad del av dessa Villkor. Genom att 
                acceptera dessa Villkor accepterar du också vår Integritetspolicy.
              </p>
              <p>
                All kommunikation inom Tjänsten är krypterad och skyddad enligt branschstandard 
                för säkerhet.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-black">9. Immateriella rättigheter</h2>
              <p>
                Tjänsten och allt relaterat innehåll, inklusive AI-teknologi, design, texter och 
                programvara, ägs av Your Therapist AB eller våra licensgivare och skyddas av 
                upphovsrätt och andra immaterialrätter.
              </p>
              <p>
                Du får en begränsad, icke-exklusiv licens att använda Tjänsten för personliga, 
                icke-kommersiella ändamål i enlighet med dessa Villkor.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-black">10. Ansvarsbegränsning och friskrivning</h2>
              <p className="font-semibold text-red-700">
                VI ANSVARAR INTE FÖR DIREKTA, INDIREKTA, SPECIELLA ELLER FÖLJDSKADOR SOM UPPSTÅR 
                FRÅN DIN ANVÄNDNING AV TJÄNSTEN.
              </p>
              <p>
                Detta inkluderar men är inte begränsat till:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personskador eller psykiska skador</li>
                <li>Förlust av inkomst eller affärsmöjligheter</li>
                <li>Dataförlust eller systemfel</li>
                <li>Skador orsakade av AI:ns råd eller bristande råd</li>
                <li>Konsekvenser av din missförståelse eller missbruk av Tjänsten</li>
              </ul>
              <p>
                Vårt totala ansvar gentemot dig ska aldrig överstiga det belopp du betalat för 
                Tjänsten under de senaste 12 månaderna.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-black">11. Uppsägning</h2>
              <p>
                Du kan när som helst avsluta ditt konto genom att kontakta vår kundtjänst. Vi kan 
                avsluta ditt konto med eller utan anledning med 30 dagars förvarning, eller omedelbart 
                vid brott mot dessa Villkor.
              </p>
              <p>
                Vid uppsägning upphör din rätt att använda Tjänsten omedelbart. Dina data kommer att 
                hanteras enligt vår Integritetspolicy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-black">12. Ändringar av villkoren</h2>
              <p>
                Vi kan när som helst ändra dessa Villkor. Väsentliga ändringar kommer att meddelas 
                via e-post eller genom Tjänsten minst 30 dagar före ikraftträdandet. Din fortsatta 
                användning av Tjänsten efter ändringar innebär att du accepterar de nya villkoren.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-black">13. Tvistlösning och tillämplig lag</h2>
              <p>
                Dessa Villkor regleras av svensk lag. Eventuella tvister ska i första hand lösas 
                genom förhandling. Om detta inte är möjligt ska tvisten avgöras av svensk domstol, 
                med Stockholms tingsrätt som första instans.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-black">14. Kontaktinformation</h2>
              <p>
                Om du har frågor om dessa Villkor eller Tjänsten, kontakta oss på:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>Your Therapist AB</strong></p>
                <p>E-post: info@yourtherapist.se</p>
                <p>Postadress: Riddargatan 70, 114 57 Stockholm</p>
              </div>
            </section>

            <section className="border-t pt-8">
              <p className="text-sm text-gray-600">
                Genom att använda Your Therapist bekräftar du att du har läst, förstått och accepterat 
                dessa Användarvillkor i sin helhet. Om du inte accepterar dessa villkor ska du inte 
                använda Tjänsten.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
